import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Avatar,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Grid,
  Alert,
  Autocomplete,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { TopBar } from "../components/ReqContainerStyles";
import { useSnackbar } from "notistack";
import { Image as ImageIcon } from "@mui/icons-material";
import SharePointManager from "./SharepointManager";

// ===== IMPORTAR SERVICIO API CENTRALIZADO =====
import { apiService, handleApiError } from '../config/api';

import logo from "./assets/image/LOGO_FCN.png";

const avatarStyle = { width: "20px", height: "20px", marginRight: "8px" };
const theme = createTheme({
  palette: {
    primary: { main: "#E19C00" },
    text: { primary: "#432918" },
    background: { default: "#432918" },
  },
});

const Registro = () => {
  const [formData, setFormData] = useState({
    fotografia: null,
    asociacion: "",
    cooperativa: "",
    departamentos: "",
    municipios: "",
    nombre_representante: "",
    domicilio: "",
    dni: "",
    ubicacion_finca: "",
    telefono: "",
    estatus: "",
    fechaPuntoActaCertif: "",
    reporteCosecha: "",
    fechaEleccion: "",
    claveIHCAFE: "",
    fechaVencimiento: "",
    observaciones: "",
    numero: "",
    tomo: "",
    libro: "",
    fecha_constancia: "",
    tipoRegistro: "nuevo",
  });

  const [options, setOptions] = useState({
    cooperativas: [],
    asociaciones: [],
    departamentos: [],
    municipios: [],
    representantes: [],
  });

  const [filteredOptions, setFilteredOptions] = useState({
    cooperativas: [],
    municipios: [],
    representantes: [],
  });

  const [errorMessages, setErrorMessages] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [imageSource, setImageSource] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const sharePointManager = new SharePointManager();
  const [searchTerm, setSearchTerm] = useState("");
  const formatDNI = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 4) {
      return cleaned;
    } else if (cleaned.length <= 8) {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    } else {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}-${cleaned.slice(8, 13)}`;
    }
  };

  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleCancelSave = () => {
    handleCloseConfirmDialog();
  };

  const calcularFechaVencimiento = (fechaEleccion) => {
    if (!fechaEleccion) return "";
    
    const fecha = new Date(fechaEleccion);
    if (isNaN(fecha.getTime())) return "";
    
    fecha.setFullYear(fecha.getFullYear() + 3);
    return fecha.toISOString().split('T')[0];
  };
  const determinarEstatus = (fechaVencimiento) => {
    if (!fechaVencimiento) return "";
    
    const fechaVenc = new Date(fechaVencimiento);
    const hoy = new Date();
    if (fechaVenc < hoy) {
      return "VENCIDA";
    } else {
      return "VIGENTE";
    }
  };

  const formatTelefono = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    const trimmed = cleaned.slice(0, 9);
    
    if (trimmed.length <= 4) {
      return trimmed;
    } else {
      return `${trimmed.slice(0, 4)}-${trimmed.slice(4)}`;
    }
  };

  const limpiarCamposFormulario = () => {
    setFormData(prev => ({
      ...prev,
      asociacion: "",
      cooperativa: "",
      departamentos: "",
      municipios: "",
      nombre_representante: "",
      domicilio: "",
      dni: "",
      ubicacion_finca: "",
      telefono: "",
      estatus: "",
      fechaPuntoActaCertif: "",
      reporteCosecha: "",
      fechaEleccion: "",
      claveIHCAFE: "",
      fechaVencimiento: "",
      observaciones: "",
      numero: "",
      tomo: "",
      libro: "",
      fecha_constancia: "",
      fotografia: null,
      fotografiaPreview: null,
    }));
    setFilteredOptions({
      cooperativas: [],
      municipios: [],
      representantes: [],
    });
    setImageFile(null);
    setImagePreview(null);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
      
        const datosFormulario = await apiService.utils.getDatosFormulario();
        
        const representantes = await apiService.representantes.getAll();
       
        setOptions({
          asociaciones: datosFormulario.asociaciones || [],
          departamentos: datosFormulario.departamentos || [],
          municipios: datosFormulario.municipios || [],
          cooperativas: datosFormulario.cooperativas || [],
          representantes: representantes || [],
        });

      } catch (error) {
        const errorMessage = handleApiError(error, "Error al cargar datos iniciales");
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (formData.fechaVencimiento) {
      const estatus = determinarEstatus(formData.fechaVencimiento);
      if (estatus !== formData.estatus) {
        setFormData(prev => ({ ...prev, estatus }));
      }
    }
  }, [formData.fechaVencimiento]);

  useEffect(() => {
    if (formData.departamentos) {
      const filteredMunicipios = options.municipios.filter(
        (municipio) => municipio.CodigoDepartamento === formData.departamentos
      );
      setFilteredOptions((prev) => ({
        ...prev,
        municipios: filteredMunicipios,
      }));
    }
  }, [formData.departamentos, options.municipios]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // ===== MANEJAR CAMBIO DE TIPO DE REGISTRO =====
    if (name === "tipoRegistro") {
      if (value !== formData.tipoRegistro) {
        // Limpiar campos cuando cambie el tipo de registro
        limpiarCamposFormulario();
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }
    
    // Formatear DNI
    if (name === "dni") {
      const formattedDNI = formatDNI(value);
      setFormData((prev) => ({ ...prev, [name]: formattedDNI }));
    } 
    // Formatear teléfono
    else if (name === "telefono") {
      const formattedTelefono = formatTelefono(value);
      setFormData((prev) => ({ ...prev, [name]: formattedTelefono }));
    }
    // Para otros campos, comportamiento normal
    else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "fechaEleccion" && value) {
      const nuevaFechaVencimiento = calcularFechaVencimiento(value);
      const nuevoEstatus = determinarEstatus(nuevaFechaVencimiento);
      
      setFormData((prev) => ({
        ...prev,
        fechaVencimiento: nuevaFechaVencimiento,
        estatus: nuevoEstatus
      }));
    }

    if (name === "asociacion") {
      filterRepresentativesByAsociacion(value);
    }

    if (
      name === "nombre_representante" &&
      formData.tipoRegistro === "reeleccion"
    ) {
      const selectedRepresentative = filteredOptions.representantes.find(
        (rep) => rep.nombre_representante === value
      );
      if (selectedRepresentative) {
        
        // ===== LLENAR TODOS LOS CAMPOS DEL REPRESENTANTE =====
        setFormData((prev) => ({
          ...prev,
          // Datos básicos del representante
          domicilio: selectedRepresentative.domicilio || "",
          telefono: selectedRepresentative.telefono || "",
          ubicacion_finca: selectedRepresentative.ubicacion_finca || "",
          dni: selectedRepresentative.dni || "",
          estatus: selectedRepresentative.estatus || "",
          
          // ===== INCLUIR DEPARTAMENTO Y MUNICIPIO =====
          departamentos: selectedRepresentative.cod_depto || "",
          municipios: selectedRepresentative.cod_municipio || "",
          
          // ===== INCLUIR COOPERATIVA SI EXISTE =====
          cooperativa: selectedRepresentative.cod_cooperativa || "",
          
          // Fechas y datos administrativos
          fechaPuntoActaCertif: selectedRepresentative.fecha_certificacion_punto_acta || "",
          reporteCosecha: selectedRepresentative.reporte_cosecha || "",
          fechaEleccion: selectedRepresentative.fecha_eleccion || "",
          claveIHCAFE: selectedRepresentative.clave_IHCAFE || "",
          fechaVencimiento: selectedRepresentative.fecha_vencimiento || "",
          observaciones: selectedRepresentative.observaciones || "",
          
          // Datos de CONSUCOOP
          numero: selectedRepresentative.numero || "",
          tomo: selectedRepresentative.tomo || "",
          libro: selectedRepresentative.libro || "",
          fecha_constancia: selectedRepresentative.fecha_constancia || "",
          
          // Fotografía
          fotografia: selectedRepresentative.fotografia || null,
        }));

        // ===== ACTUALIZAR MUNICIPIOS FILTRADOS SEGÚN EL DEPARTAMENTO =====
        if (selectedRepresentative.cod_depto) {
          const filteredMunicipios = options.municipios.filter(
            (municipio) => municipio.CodigoDepartamento === selectedRepresentative.cod_depto
          );
          
          setFilteredOptions((prev) => ({
            ...prev,
            municipios: filteredMunicipios,
          }));
          
        }

        // ===== ACTUALIZAR COOPERATIVAS FILTRADAS SEGÚN LA ASOCIACIÓN =====
        if (selectedRepresentative.cod_asociacion) {
          const filteredCoops = options.cooperativas.filter(
            (coop) => coop.cod_asociacion === selectedRepresentative.cod_asociacion
          );
          
          setFilteredOptions((prev) => ({
            ...prev,
            cooperativas: filteredCoops,
          }));
        }
      }
    }
  };
  
  const filterRepresentativesByAsociacion = (asociacionValue) => {
    if (asociacionValue) {
      const filteredCoops = options.cooperativas.filter(
        (coop) => coop.cod_asociacion === asociacionValue
      );
      const filteredRepresentatives = options.representantes.filter(
        (rep) => rep.cod_asociacion === asociacionValue
      );

      setFilteredOptions((prev) => ({
        ...prev,
        cooperativas: filteredCoops,
        representantes: filteredRepresentatives,
      }));

      setFormData((prev) => ({
        ...prev,
        cooperativa: "",
        nombre_representante: "",
      }));
    } else {
      setFilteredOptions((prev) => ({
        ...prev,
        cooperativas: [],
        representantes: [],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar el formulario antes de proceder
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Por favor, corrija los errores en el formulario",
        severity: "error",
      });
      return;
    }
    setOpenConfirmDialog(true);
  };
  const handleConfirmSave = async () => {
    setLoading(true);

    try {
      let imageUrl = formData.fotografia;
      if (imageFile) {
        try {
          await sharePointManager.initialize();
          const uploadResult = await sharePointManager.uploadFile(
            imageFile,
            formData.nombre_representante
          );
          imageUrl = uploadResult.webUrl;
        } catch (uploadError) {
        }
      }

      // ===== PREPARAR DATOS PARA ENVÍO =====
      const dataToSend = {
        cod_depto: formData.departamentos || "",
        // El backend espera cod_municipio pero debe ser un valor que exista en CodigoMunicipio
        cod_municipio: formData.municipios || "",
        cod_asociacion: formData.asociacion ? parseInt(formData.asociacion, 10) : null,
        nombre_representante: formData.nombre_representante || null,
        dni: formData.dni || null,
        domicilio: formData.domicilio || null,
        ubicacion_finca: formData.ubicacion_finca || null,
        telefono: formData.telefono || null,
        estatus: formData.estatus || null,
        fecha_certificacion_punto_acta: formData.fechaPuntoActaCertif || null,
        clave_IHCAFE: formData.claveIHCAFE || null,
        reporte_cosecha: formData.reporteCosecha || null,
        fecha_eleccion: formData.fechaEleccion || null,
        fecha_vencimiento: formData.fechaVencimiento || null,
        observaciones: formData.observaciones || null,
        fotografia: imageUrl,
        cod_cooperativa: formData.cooperativa || null,
        numero: formData.numero || null,
        tomo: formData.tomo || null,
        libro: formData.libro || null,
        fecha_constancia: formData.fecha_constancia || null,
      };
      const response = await apiService.representantes.create(dataToSend);
      
      setSnackbar({
        open: true,
        message: "Registro creado exitosamente",
        severity: "success",
      });

      // Resetear formulario
      setFormData({
        fotografia: null,
        asociacion: "",
        cooperativa: "",
        departamentos: "",
        municipios: "",
        nombre_representante: "",
        domicilio: "",
        dni: "",
        ubicacion_finca: "",
        telefono: "",
        estatus: "",
        fechaPuntoActaCertif: "",
        reporteCosecha: "",
        fechaEleccion: "",
        claveIHCAFE: "",
        fechaVencimiento: "",
        observaciones: "",
        numero: "",
        tomo: "",
        libro: "",
        fecha_constancia: "",
        tipoRegistro: "nuevo",
      });

      setImageFile(null);
      setImagePreview(null);
      setFilteredOptions({
        cooperativas: [],
        municipios: [],
        representantes: [],
      });

    } catch (error) {
      const getErrorMessage = (errorString) => {
        if (errorString.includes("notNull Violation")) {
          // Extraer los nombres de los campos
          const camposRegex = /representantes\.([a-z_]+) cannot be null/gi;
          const matches = [...errorString.matchAll(camposRegex)];
          
          if (matches.length > 0) {
            const camposFaltantes = matches.map(match => {
              const campo = match[1];
              // Traducir nombres de campos técnicos a nombres amigables
              const nombresCampos = {
                'cod_asociacion': 'Asociación',
                'nombre_representante': 'Nombre del representante',
                'cod_depto': 'Departamento',
                'cod_municipio': 'Municipio',
                'telefono': 'Teléfono',
                'dni': 'DNI',
                'estatus': 'Estatus',
                'domicilio': 'Domicilio'
                // Añade más campos según sea necesario
              };
              
              return nombresCampos[campo] || campo;
            });
            
            if (camposFaltantes.length === 1) {
              return `El campo ${camposFaltantes[0]} es obligatorio.`;
            } else {
              return `Los siguientes campos son obligatorios: ${camposFaltantes.join(', ')}.`;
            }
          }
        }
        
        // Error de clave foránea (cuando se hace referencia a un ID que no existe)
        if (errorString.includes("foreign key constraint fails")) {
          if (errorString.includes("representantes_cod_depto_fkey")) {
            return "El departamento seleccionado no es válido.";
          }
          if (errorString.includes("representantes_cod_municipio_fkey")) {
            return "El municipio seleccionado no es válido.";
          }
          if (errorString.includes("representantes_cod_asociacion_fkey")) {
            return "La asociación seleccionada no es válida.";
          }
          return "Error en las referencias seleccionadas. Verifique los datos ingresados.";
        }
        
        // Para otros tipos de errores de validación
        if (errorString.includes("Validation error")) {
          return "Error de validación. Verifique que todos los campos tengan el formato correcto.";
        }
        
        // Mensaje genérico si no identificamos un error específico
        return "Ocurrió un error al guardar los datos. Por favor revise la información e intente nuevamente.";
      };
      
      // Extraer el mensaje de error
      let errorMessage = "Error desconocido al guardar el registro.";
      
      // Primero intentar usar el manejador centralizado
      errorMessage = handleApiError(error, "Error al guardar el registro");
      
      // Si el error contiene información específica del backend, usar nuestra función personalizada
      if (error.message && (error.message.includes("notNull") || error.message.includes("foreign key"))) {
        errorMessage = getErrorMessage(error.message);
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });

    } finally {
      setLoading(false);
      handleCloseConfirmDialog();
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.departamentos) {
      errors.departamentos = "El departamento es requerido.";
    }
    if (!formData.municipios) {
      errors.municipios = "El municipio es requerido.";
    }
    if (!formData.asociacion) {
      errors.asociacion = "La asociación es requerida.";
    }
    if (!formData.nombre_representante) {
      errors.nombre_representante = "El nombre del representante es requerido.";
    }
    const dniPattern = /^\d{4}-\d{4}-\d{5}$/;
    if (formData.dni && !dniPattern.test(formData.dni)) {
      errors.dni = "El DNI debe tener el formato 0000-0000-00000.";
    }

    ["fechaPuntoActaCertif", "fechaEleccion", "fechaVencimiento"].forEach(
      (dateField) => {
        if (isNaN(new Date(formData[dateField]))) {
          errors[dateField] = `La fecha de ${dateField} no es válida.`;
        }
      }
    );

    if (
      new Date(formData.fechaVencimiento) <= new Date(formData.fechaEleccion)
    ) {
      errors.fechaVencimiento =
        "La fecha de vencimiento debe ser mayor a la fecha de elección.";
    }
    if (new Date(formData.fechaPuntoActaCertif) > new Date()) {
      errors.fechaPuntoActaCertif =
        "La fecha de certificación no puede ser futura.";
    }
    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setFormData((prev) => ({
        ...prev,
        fotografiaPreview: URL.createObjectURL(file),
      }));
    } else {
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        maxWidth="md"
        sx={{
          marginTop: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "10px",
        }}
      >
        <TopBar
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <img
            src={logo}
            alt="Logo FCN"
            style={{ maxWidth: "120px", marginRight: "5px" }}
          />
          <Typography
            variant="h5"
            style={{ color: "#432918", fontWeight: "bold" }}
          >
            {" "}
            Registro de representantes
          </Typography>
        </TopBar>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* ===== FOTOGRAFÍA ===== */}
            <Grid item xs={12} sm={6}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Avatar
                  src={
                    formData.fotografiaPreview ||
                    formData.fotografia ||
                    "/placeholder-avatar.png"
                  }
                  alt="Fotografía del Representante"
                  style={{ width: 100, height: 100, marginBottom: 10 }}
                />

                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="raised-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="raised-button-file">
                  <Button variant="contained" component="span">
                    Cargar Fotografía
                  </Button>
                </label>
              </Box>
            </Grid>

            {/* ===== TIPO DE REGISTRO ===== */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Tipo de Registro</InputLabel>
                <Select
                  label="Tipo de Registro"
                  name="tipoRegistro"
                  value={formData.tipoRegistro}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="nuevo">Nuevo Representante</MenuItem>
                  <MenuItem value="reeleccion">Reelección</MenuItem>
                </Select>
              </FormControl>
            </Grid>
           
            <Grid item xs={12} sm={6}>

              <FormControl fullWidth>
                <InputLabel>Asociación *</InputLabel>
                <Select
                  label="Asociación"
                  name="asociacion"
                  value={formData.asociacion}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">
                    <em>Seleccione...</em>
                  </MenuItem>
                  {options.asociaciones.map((asociacion) => (
                    <MenuItem
                      key={asociacion.cod_asociacion}
                      value={asociacion.cod_asociacion}
                    >
                      {asociacion.nombre_abreviado}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ===== NOMBRE DEL REPRESENTANTE ===== */}
            <Grid item xs={12} sm={6}>
              {formData.tipoRegistro === "nuevo" ? (
                <TextField
                  label="Nombre del nuevo representante *"
                  name="nombre_representante"
                  value={formData.nombre_representante}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              ) : (
                <Autocomplete
                  options={filteredOptions.representantes}
                  getOptionLabel={(option) => option.nombre_representante}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      
                      // ===== LLENAR TODOS LOS CAMPOS DEL REPRESENTANTE =====
                      setFormData((prev) => ({
                        ...prev,
                        // Datos básicos del representante
                        nombre_representante: newValue.nombre_representante || "",
                        domicilio: newValue.domicilio || "",
                        telefono: newValue.telefono || "",
                        ubicacion_finca: newValue.ubicacion_finca || "",
                        dni: newValue.dni || "",
                        estatus: newValue.estatus || "",
                        
                        // ===== INCLUIR DEPARTAMENTO Y MUNICIPIO =====
                        departamentos: newValue.cod_depto || "",
                        municipios: newValue.cod_municipio || "",
                        
                        // ===== INCLUIR COOPERATIVA SI EXISTE =====
                        cooperativa: newValue.cod_cooperativa || "",
                        
                        // Fechas y datos administrativos
                        fechaPuntoActaCertif: newValue.fecha_certificacion_punto_acta || "",
                        reporteCosecha: newValue.reporte_cosecha || "",
                        fechaEleccion: newValue.fecha_eleccion || "",
                        claveIHCAFE: newValue.clave_IHCAFE || "",
                        fechaVencimiento: newValue.fecha_vencimiento || "",
                        observaciones: newValue.observaciones || "",
                        
                        // Datos de CONSUCOOP
                        numero: newValue.numero || "",
                        tomo: newValue.tomo || "",
                        libro: newValue.libro || "",
                        fecha_constancia: newValue.fecha_constancia || "",
                        
                        // Fotografía
                        fotografia: newValue.fotografia || null,
                      }));

                      // ===== ACTUALIZAR MUNICIPIOS FILTRADOS SEGÚN EL DEPARTAMENTO =====
                      if (newValue.cod_depto) {
                        const filteredMunicipios = options.municipios.filter(
                          (municipio) => municipio.CodigoDepartamento === newValue.cod_depto
                        );
                        
                        setFilteredOptions((prev) => ({
                          ...prev,
                          municipios: filteredMunicipios,
                        }));
                        
                      }

                      // ===== ACTUALIZAR COOPERATIVAS FILTRADAS SEGÚN LA ASOCIACIÓN =====
                      if (newValue.cod_asociacion) {
                        const filteredCoops = options.cooperativas.filter(
                          (coop) => coop.cod_asociacion === newValue.cod_asociacion
                        );
                        
                        setFilteredOptions((prev) => ({
                          ...prev,
                          cooperativas: filteredCoops,
                        }));
                      }
                      
                    } else {
                      // Si no hay un nuevo valor, restablece los campos relacionados
                      setFormData((prev) => ({
                        ...prev,
                        nombre_representante: "",
                        domicilio: "",
                        telefono: "",
                        ubicacion_finca: "",
                        dni: "",
                        estatus: "",
                        departamentos: "",
                        municipios: "",
                        cooperativa: "",
                        fechaPuntoActaCertif: "",
                        reporteCosecha: "",
                        fechaEleccion: "",
                        claveIHCAFE: "",
                        fechaVencimiento: "",
                        observaciones: "",
                        numero: "",
                        tomo: "",
                        libro: "",
                        fecha_constancia: "",
                        fotografia: null,
                      }));
                      
                      // Limpiar filtros
                      setFilteredOptions((prev) => ({
                        ...prev,
                        municipios: [],
                        cooperativas: [],
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Nombre del representante *"
                      fullWidth
                      required
                    />
                  )}
                  filterOptions={(options, { inputValue }) => {
                    const filtered = options.filter((option) =>
                      option.nombre_representante.toLowerCase().includes(inputValue.toLowerCase())
                    );
                    return filtered;
                  }}
                  noOptionsText="No hay opciones"
                  disabled={!formData.asociacion}
                />
              )}
            </Grid>

            {/* ===== COOPERATIVA ===== */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Cooperativa</InputLabel>
                <Select
                  label="Cooperativa"
                  name="cooperativa"
                  value={formData.cooperativa}
                  onChange={handleChange}
                  disabled={!formData.asociacion}
                >
                  <MenuItem value="">
                    <em>Seleccione...</em>
                  </MenuItem>
                  {filteredOptions.cooperativas.map((coop) => (
                    <MenuItem
                      key={coop.cod_cooperativa}
                      value={coop.cod_cooperativa}
                    >
                      {coop.nombre_cooperativa}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
                label="DNI del representante"
                name="dni"
                value={formData.dni}
                onChange={handleChange}
                fullWidth
                error={!!errorMessages.dni}
                helperText={errorMessages.dni}
                placeholder="Ej: 0000-0000-00000"
                inputProps={{
                  maxLength: 15  // 13 dígitos + 2 guiones = 15 caracteres
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                fullWidth
                error={!!errorMessages.telefono}
                helperText={errorMessages.telefono}
                placeholder="0000-0000"
                inputProps={{
                  maxLength: 9  // 8 dígitos + 1 guion = 9 caracteres
                }}
              />
            </Grid>


            {/* ===== SECCIÓN UBICACIÓN ===== */}
           

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Departamento *</InputLabel>
                <Select
                  label="Departamento"
                  name="departamentos"
                  value={formData.departamentos}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">
                    <em>Seleccione...</em>
                  </MenuItem>
                  {options.departamentos.map((departamento) => (
                    <MenuItem
                      key={departamento.CodigoDepartamento}
                      value={departamento.CodigoDepartamento}
                    >
                      {departamento.NombreDepartamento}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Municipio *</InputLabel>
                <Select
                  label="Municipio"
                  name="municipios"
                  value={formData.municipios}
                  onChange={handleChange}
                  required
                  disabled={!formData.departamentos}
                >
                  <MenuItem value="">
                    <em>Seleccione...</em>
                  </MenuItem>
                  {filteredOptions.municipios.map((municipio) => (
                    <MenuItem
                      key={municipio.CodigoMunicipio}
                      value={municipio.CodigoMunicipio}
                    >
                      {municipio.NombreMunicipio}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
              <Grid item xs={12} sm={6}>
              <TextField
                label="Domicilio del representante"
                name="domicilio"
                value={formData.domicilio}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Ubicación de finca"
                name="ubicacion_finca"
                value={formData.ubicacion_finca}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            {/* ===== SECCIÓN DATOS PERSONALES ===== */}
            
          
          

            {/* ===== SECCIÓN DATOS ADMINISTRATIVOS ===== */}
           

            <Grid item xs={12} sm={6}>
              <TextField
                label="Reporte de cosecha"
                name="reporteCosecha"
                value={formData.reporteCosecha}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Clave IHCAFE"
                name="claveIHCAFE"
                value={formData.claveIHCAFE}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de certificación"
                type="date"
                name="fechaPuntoActaCertif"
                value={formData.fechaPuntoActaCertif}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errorMessages.fechaPuntoActaCertif}
                helperText={errorMessages.fechaPuntoActaCertif}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de elección"
                type="date"
                name="fechaEleccion"
                value={formData.fechaEleccion}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errorMessages.fechaEleccion}
                helperText={errorMessages.fechaEleccion}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de vencimiento"
                type="date"
                name="fechaVencimiento"
                value={formData.fechaVencimiento}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={true}
                error={!!errorMessages.fechaVencimiento}
                helperText={errorMessages.fechaVencimiento}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estatus</InputLabel>
                <Select
                  label="Estatus"
                  name="estatus"
                  value={formData.estatus}
                  onChange={handleChange}
                  required
                  disabled={true} // Opcional: deshabilitar si hay fecha de vencimiento
                >
                  <MenuItem value="">
                    <em>Seleccione...</em>
                  </MenuItem>
                  <MenuItem value="VIGENTE">VIGENTE</MenuItem>
                  <MenuItem value="VENCIDA">VENCIDA</MenuItem>
                </Select>
              </FormControl>
              {formData.fechaVencimiento && (
                <Typography variant="caption" color="textSecondary">
                  El estatus se determina automáticamente según la fecha de vencimiento
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Observaciones"
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
              />
            </Grid>

            {/* ===== SECCIÓN CONSUCOOP (SOLO SI HAY COOPERATIVA) ===== */}
            {formData.cooperativa && (
              <>
              
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Número"
                    name="numero"
                    value={formData.numero}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Tomo"
                    name="tomo"
                    value={formData.tomo}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Libro"
                    name="libro"
                    value={formData.libro}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fecha de constancia de CONSUCOOP"
                    type="date"
                    name="fecha_constancia"
                    value={formData.fecha_constancia}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ marginTop: 4 }}
            >
              {loading ? "Guardando..." : "Guardar Registro"}
            </Button>
          </Grid>
        </form>

        <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
          <DialogTitle>Confirmación</DialogTitle>
          <DialogContent>
            <p>¿Está seguro de que desea guardar este registro?</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSave} color="primary" disabled={loading}>
              No
            </Button>
            <Button onClick={handleConfirmSave} color="primary" disabled={loading}>
              {loading ? "Guardando..." : "Sí"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default Registro;